import {
  Close,
  Content,
  type DialogContentProps as RadixDialogContentProps,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger
} from "@radix-ui/react-dialog"
import clsx from "clsx"
import {
  createContext,
  type ReactNode,
  type RefObject,
  useContext,
  useRef
} from "react"

import { Button, type ButtonProps } from "../Button"
import { Icon, type IconName } from "../Icon"
import { IconButton } from "../IconButton"
import { Text, type TextProps } from "../Text"
import { Tooltip } from "../Tooltip"
import styles from "./Dialog.module.css"
import { FullscreenDialog } from "./FullscreenDialog"

export interface Custom {
  width: number | string
  height: number | string
}
export interface DialogProps {
  /* Modal or non-modal Dialog */
  modal?: boolean
  /* The element that will trigger the Dialog opening and be re-focused
     (only for modal type) when the Dialog is closed. */
  trigger?: ReactNode
  children?: ReactNode
  /* A callback to wrap the dialog trigger with a tooltip whilst allowing
      the trigger to re-focus on dialog close. */
  renderTooltip?: (trigger: ReactNode) => ReactNode
  /* Control the Dialog open state. You only need this if you require a manual way to close
     the dialog other than the default Close button, hitting the escape key, etc. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onAnimationEnd?: () => void
  onAnimationStart?: () => void
  hasClose?: boolean
  size?: "small" | "large" | "fullscreen" | Custom

  /**
   * The position of the dialog in the viewport.
   * @default "top" - The dialog is positioned at the center, top of the viewport. This covers most cases.
   * "center" - The dialog is centered in the viewport. Useful for large, almost fullscreen dialogs.
   */
  position?: "center" | "bottomRight" | "top"
  onPointerDownOutside?: RadixDialogContentProps["onPointerDownOutside"]
  onOpenAutoFocus?: RadixDialogContentProps["onOpenAutoFocus"]
}

/**
 * A Dialog rendered in document.body.
 * The first focusable child will automatically be focused.
 */
export function Dialog(props: DialogProps) {
  const {
    trigger,
    children,
    renderTooltip = (node) => node,
    onAnimationEnd,
    onAnimationStart,
    size = "large",
    hasClose = true,
    modal = true,
    position = "top",
    onPointerDownOutside,
    onOpenAutoFocus,
    onOpenChange,
    ...otherProps
  } = props

  function handleDefaultInteraction(evt: Event) {
    if (size === "fullscreen" || modal === false) evt.preventDefault()
  }

  const topOffset = useTopOffset()

  const contentRef = useRef<HTMLDivElement>(null)

  if (size === "fullscreen")
    return (
      <FullscreenDialog {...props} onEscapeKeyDown={handleDefaultInteraction} />
    )

  const isDialogVariant = size === "large" || size === "small"

  const dialogDimensions = !isDialogVariant ? size : undefined

  const content = (
    <Content
      style={{ ...dialogDimensions, top: topOffset }}
      className={clsx(
        styles.container,
        styles[`container-${position}`],
        isDialogVariant && styles[`container-${size}`],
        topOffset && styles.child
      )}
      onAnimationStart={onAnimationStart}
      onAnimationEnd={onAnimationEnd}
      onEscapeKeyDown={handleDefaultInteraction}
      onOpenAutoFocus={onOpenAutoFocus ?? handleDefaultInteraction}
      onCloseAutoFocus={handleDefaultInteraction}
      onInteractOutside={(event) => event.preventDefault()}
      onPointerDownOutside={onPointerDownOutside}
      ref={contentRef}
    >
      <DialogContext.Provider value={{ parentDialog: contentRef }}>
        {children}
      </DialogContext.Provider>

      {/* The close button should be the last focusable item of the Dialog
  so that a meaningful element is focused instead. */}
      {hasClose && (
        <div className={styles.closeButton}>
          <Close asChild>
            <IconButton icon="close" hiddenLabel="Close dialog" />
          </Close>
        </div>
      )}
    </Content>
  )

  // Overlay wraps dialog content to ensure scroll event's of Portals within Dialog Content can be picked up
  // https://github.com/radix-ui/primitives/issues/1159
  return (
    <Root modal={modal} onOpenChange={onOpenChange} {...otherProps}>
      {renderTooltip(trigger && <Trigger asChild>{trigger}</Trigger>)}

      <Portal>
        {modal && (
          <Overlay
            className={styles.overlay}
            onClick={() => onOpenChange?.(false)}
          />
        )}
        {content}
      </Portal>
    </Root>
  )
}

interface DialogHeaderProps {
  icon?: IconName
  onIconClick?: () => void
  title: string
}

export function DialogHeader(props: DialogHeaderProps) {
  const { icon, onIconClick, title } = props

  return (
    <header className={styles.header}>
      {icon && <DialogHeaderIcon icon={icon} onClick={onIconClick} />}
      <Title asChild>
        <Text noSelect component="span" variant="bold">
          {title}
        </Text>
      </Title>
    </header>
  )
}

interface DialogHeaderIconProps {
  icon: IconName
  onClick?: () => void
}

function DialogHeaderIcon(props: DialogHeaderIconProps) {
  const { icon, onClick } = props

  if (onClick) return <IconButton icon={icon} onClick={onClick} />

  return (
    // Makes sure it has the same size as the IconButton so it looks nice when
    // we transition between headers with and without button.
    <div className={styles.headerIcon}>
      <Icon name={icon} size="medium" color="grey11" />
    </div>
  )
}

interface DialogContentProps {
  children: ReactNode
  padding?: 16
  gap?: 0 | 12 | 16
}

export function DialogContent(props: DialogContentProps) {
  const { children, padding, gap = 12, ...implicitProps } = props

  return (
    <section
      {...implicitProps}
      className={clsx(
        styles.content,
        padding && styles[`contentPadding-${padding}`],
        gap && styles[`contentGap-${gap}`]
      )}
    >
      {children}
    </section>
  )
}

export function DialogActions(props: { children: ReactNode }) {
  const { children } = props
  return <section className={styles.actions}>{children}</section>
}

interface DialogActionButtonProps extends ButtonProps {
  tooltipLabel?: string
}

export function DialogActionButton(props: DialogActionButtonProps) {
  const {
    tooltipLabel,
    size = "large",
    color = "purple",
    children,
    ...remainingButtonProps
  } = props

  return (
    <Tooltip visible={Boolean(tooltipLabel)} content={tooltipLabel ?? ""}>
      <Button size={size} color={color} {...remainingButtonProps}>
        {children}
      </Button>
    </Tooltip>
  )
}

export function DialogActionLink(props: TextProps) {
  const { variant = "small", children, ...remainingTextProps } = props

  return (
    <Text variant={variant} {...remainingTextProps} sx={{ flexGrow: 1 }}>
      {children}
    </Text>
  )
}

export function DialogCancelButton(props: ButtonProps) {
  const {
    size = "large",
    color = "grey",
    children = "Cancel",
    ...remainingButtonProps
  } = props

  return (
    <Close asChild>
      <Button color={color} size={size} {...remainingButtonProps}>
        {children}
      </Button>
    </Close>
  )
}

const DialogContext = createContext<{
  parentDialog: RefObject<HTMLDivElement> | null
}>({ parentDialog: null })

function useTopOffset() {
  const { parentDialog } = useContext(DialogContext)

  const parentDimensions = parentDialog?.current?.getBoundingClientRect()

  if (!parentDimensions) return undefined

  const { height, top } = parentDimensions

  // The top offset is the center of the parent dialog, translated -50% in the CSS file.
  return height / 2 + top
}
